import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import usersApi from "@/api/usersApi";
import ApiError from "@/model/ApiError";
import "@/components/ChangePasswordForm/ChangePasswordForm.scss";

function ChangePasswordForm() {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!session) {
    return null;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    if (newPassword !== newPasswordConfirmation) {
      setError(t("users.profile.changePassword.mismatch"));
      return;
    }

    setIsSubmitting(true);

    try {
      await usersApi.changePassword(session.accessToken, {
        currentPassword,
        newPassword,
        newPasswordConfirmation,
      });
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirmation("");
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("users.profile.changePassword.invalidRequest"));
      } else {
        setError(t("users.profile.changePassword.genericError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="change-password-form" onSubmit={handleSubmit}>
      <h2>{t("users.profile.changePassword.title")}</h2>

      <label htmlFor="currentPassword">
        {t("users.profile.changePassword.currentPasswordLabel")}
      </label>
      <input
        id="currentPassword"
        type="password"
        value={currentPassword}
        onChange={(event) => setCurrentPassword(event.target.value)}
        required
      />

      <label htmlFor="newPassword">
        {t("users.profile.changePassword.newPasswordLabel")}
      </label>
      <input
        id="newPassword"
        type="password"
        value={newPassword}
        onChange={(event) => setNewPassword(event.target.value)}
        minLength={8}
        required
      />

      <label htmlFor="newPasswordConfirmation">
        {t("users.profile.changePassword.newPasswordConfirmationLabel")}
      </label>
      <input
        id="newPasswordConfirmation"
        type="password"
        value={newPasswordConfirmation}
        onChange={(event) => setNewPasswordConfirmation(event.target.value)}
        minLength={8}
        required
      />

      {error && <p className="change-password-form__error">{error}</p>}
      {success && (
        <p className="change-password-form__success">
          {t("users.profile.changePassword.success")}
        </p>
      )}

      <button type="submit" disabled={isSubmitting}>
        {isSubmitting
          ? t("users.profile.changePassword.submitting")
          : t("users.profile.changePassword.submit")}
      </button>
    </form>
  );
}

export default ChangePasswordForm;
